import React, { useState } from 'react';
import { ChevronUp, Link2, FileText, Mic } from 'lucide-react';
import { getYouTubeId, fmtBytes } from './slateUtils';
import { ImageLightbox } from '../ImageLightbox';
import { AuthImage } from '../AuthImage';
import { useDownloadUrl } from '../../lib/dtoken';

function RichContent({ content }) {
  if (!content) return null;
  const parts = content.split(/(\s)/);
  return (
    <span>
      {parts.map((word, i) => {
        if (word.startsWith('@')) return <span key={i} className="text-blue-400 font-medium">{word}</span>;
        if (word.startsWith('#')) return <span key={i} className="text-[#e8c547] font-medium">{word}</span>;
        return <span key={i}>{word}</span>;
      })}
    </span>
  );
}

// Voice attachment — needs an authenticated URL for the <audio> src attribute.
// SEC-002 (2026-07-06): use dtoken via useDownloadUrl hook.
function VoiceAttachment({ att }) {
  const { url } = useDownloadUrl(att.storage_path);
  return (
    <div className="flex items-center gap-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl px-3 py-2 max-w-xs">
      <Mic className="h-4 w-4 text-[#e8c547] shrink-0" />
      {url ? (
        <audio controls className="h-8 flex-1" src={url} />
      ) : (
        <span className="text-xs text-[#555] flex-1">Loading…</span>
      )}
    </div>
  );
}

// File attachment — <a href> download. Mint dtoken once and use it.
function FileAttachment({ att }) {
  const { url } = useDownloadUrl(att.storage_path);
  return (
    <a
      href={url || '#'}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl px-3 py-2 max-w-xs hover:border-[#e8c547]/40 transition-colors"
    >
      <FileText className="h-4 w-4 text-[#a0a0a0] shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs text-[#f0f0f0] truncate">{att.filename}</div>
        {att.size && <div className="text-[10px] text-[#555]">{fmtBytes(att.size)}</div>}
      </div>
    </a>
  );
}

function AttachmentView({ att /* token unused post-SEC-002 */ }) {
  const [lightbox, setLightbox] = useState(null);
  const [videoExpanded, setVideoExpanded] = useState(false);

  if (!att) return null;
  const vid = (att.type === 'video_link' || att.type === 'link') ? getYouTubeId(att.url || att.filename) : null;

  if (att.type === 'image') {
    return (
      <>
        <AuthImage
          path={att.storage_path}
          alt={att.filename}
          className="max-h-48 max-w-xs rounded-lg cursor-pointer hover:opacity-90 border border-[#2a2a2a]"
          onClick={() => setLightbox([{ path: att.storage_path, alt: att.filename }])}
        />
        {lightbox && <ImageLightbox images={lightbox} initialIndex={0} onClose={() => setLightbox(null)} />}
      </>
    );
  }

  if (att.type === 'voice') {
    return <VoiceAttachment att={att} />;
  }

  if (att.type === 'file') {
    return <FileAttachment att={att} />;
  }

  if (vid) {
    return (
      <div className="max-w-sm rounded-lg overflow-hidden border border-[#2a2a2a]">
        {!videoExpanded ? (
          <div className="relative cursor-pointer" onClick={() => setVideoExpanded(true)}>
            <img src={`https://img.youtube.com/vi/${vid}/mqdefault.jpg`} alt="Video" className="w-full" />
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center hover:bg-black/20 transition-colors">
              <div className="h-10 w-10 bg-red-600 rounded-full flex items-center justify-center">
                <svg className="h-5 w-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe className="absolute inset-0 w-full h-full" src={`https://www.youtube.com/embed/${vid}?autoplay=1`} allow="autoplay;encrypted-media" allowFullScreen title={att.filename} />
            </div>
            <button onClick={() => setVideoExpanded(false)} className="text-xs text-[#555] px-2 py-1 flex items-center gap-1">
              <ChevronUp className="h-3 w-3" /> Collapse
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <a href={att.url || att.filename} target="_blank" rel="noreferrer"
      className="flex items-center gap-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl px-3 py-2 max-w-xs hover:border-blue-400/40 transition-colors">
      <Link2 className="h-4 w-4 text-blue-400 shrink-0" />
      <span className="text-xs text-blue-400 truncate">{att.url || att.filename}</span>
    </a>
  );
}

export { RichContent, AttachmentView };
